import { Button, Drawer } from "@components";
import { useEffect, useState } from "react";
import styles from "styles/pages/reports/reports.module.scss";
import { BubbleChart } from "pages/Reports/BubbleChart";
import { EngagementGroupChart } from "./EngagementGroupsChart";
import { Numbers } from "shared/utils/Numbers";
import { Trend } from "pages/Reports/Reports";
import dayjs from "dayjs";
import { ReportsAPI } from "providers/ReportsAPI";

const categories = (data, isCashback) => {
  return [
    {
      alias: "NUNCA_GANHOU_CASHBACK",
      name: isCashback ? "Nunca ganhou cashback" : "Nunca ganhou pontos",
      borderColor: "#fff",
      valueColor: "rgba(255, 255, 255, 0.15)",
      bgColor: "#2a2a2a",
      total: data?.never_received?.value,
      growth: data?.never_received?.growth,
    },
    {
      alias: "GANHOU_CASHBACK_NUNCA_USOU",
      name: isCashback
        ? "Ganhou cashback e nunca usou"
        : "Ganhou pontos e nunca usou",
      borderColor: "#a67666",
      valueColor: "rgba(166, 118, 102, 0.15)",
      bgColor: "#252322",
      total: data?.never_used?.value,
      growth: data?.never_used?.growth,
    },
    {
      alias: "USOU_AO_MENOS_1_VEZ",
      name: isCashback ? "Usou ao menos 1 vez" : "Usou pontos ao menos 1 vez",
      borderColor: "#d9d26c",
      valueColor: "rgba(217, 210, 108, 0.15)",
      bgColor: "#282722",
      total: data?.used_more_than_a_time?.value,
      growth: data?.used_more_than_a_time?.growth,
    },
    {
      alias: "USOU_AO_MENOS_3_VEZES",
      name: isCashback
        ? "Usou ao menos 3 vezes"
        : "Usou pontos ao menos 3 vezes",
      borderColor: "#de52b4",
      valueColor: "rgba(222, 82, 180, 0.15)",
      bgColor: "#282126",
      total: data?.used_more_than_three_times?.value,
      growth: data?.used_more_than_three_times?.growth,
    },
    {
      alias: "USOU_AO_MENOS_10_VEZES",
      name: isCashback
        ? "Usou ao menos 10 vezes"
        : "Usou pontos ao menos 10 vezes",
      borderColor: "#4bca97",
      valueColor: "rgba(81, 217, 163, 0.15)",
      bgColor: "#212825",
      total: data?.used_more_than_ten_times?.value,
      growth: data?.used_more_than_ten_times?.growth,
    },
  ];
};

export default function EngagementGroups({ data = {}, isCashback }) {
  const [selectedData, setSelectedData] = useState(null);
  const [loadingHistoric, setLoadingHistoric] = useState(false);

  const fetchGroupHistory = async (payload) => {
    setLoadingHistoric(true);
    const provider = new ReportsAPI();
    const results = await provider.loyaltyEngagementsHistory(
      "groups",
      payload.year
    );

    const limit = 12;

    const rows = Array(limit).fill(null);

    if (!results || !results.length) {
      setLoadingHistoric(false);
      return rows;
    }

    for (const result of results) {
      if (!result?.month) continue;

      const format = "MM-YYYY";
      const date = dayjs(result.month, format);
      const index = date.month() + 1;

      if (index >= 1 && index <= limit) {
        rows[index - 1] = Object.fromEntries(
          Object.entries(result)
            .map(([entryKey, obj]) => {
              if (entryKey === "month") return null;
              return [entryKey, Number(obj?.value ?? 0)];
            })
            .filter(Boolean)
        );
      }
    }

    setLoadingHistoric(false);
    return rows;
  };

  return (
    <div className={styles.box}>
      <div className="d-flex align-center justify-between mb-20">
        <div className="d-flex align-center relative gap-5">
          <p className={styles.label}>Grupos de engajamento</p>
          <span
            className={styles.info}
            title={`Quantidade de clientes por grupo com determinado comportamento envolvendo ${
              isCashback ? "cashback" : "pontos"
            }.`}
          >
            <i className="fal fa-info-circle" />
          </span>
        </div>
        <Button
          design="default-icon-mini-3"
          onClick={() =>
            setSelectedData({
              name: "Grupos de engajamento",
            })
          }
        >
          <i className="fas fa-chart-waterfall" />
        </Button>
      </div>

      <div className={styles.container}>
        <div className={styles.customers}>
          <div className={styles.grid}>
            {categories(data, isCashback).map((category, index) => {
              const total = categories(data).reduce(
                (acc, item) => acc + Number(item.total),
                0
              );

              const percent = categories(data).map((item) => {
                const percentual = (Number(item.total) / total) * 100;
                return percentual.toFixed(2);
              });

              const percentNumber = parseFloat(
                Numbers.number(percent[index]).replace(",", ".")
              );

              return (
                <div className={styles.category} key={category.alias}>
                  <div
                    className={styles.border}
                    style={{
                      backgroundColor: category.borderColor || "#fff",
                    }}
                  />
                  <div className="d-flex align-center">
                    <span
                      className={styles.medal}
                      style={{
                        backgroundColor: category.valueColor || "#fff",
                        color: category.borderColor || "#000",
                      }}
                    >
                      <i className="fal fa-medal" />
                    </span>
                    <p
                      className={styles.percent}
                      style={{ backgroundColor: category.valueColor || "#fff" }}
                    >
                      {Numbers.number(percent[index])}%
                    </p>
                    <p className={styles.name}>{category.name}</p>
                    <span className={styles.info} title={category.tooltip}>
                      <i className="fal fa-info-circle" />
                    </span>
                  </div>
                  <div
                    className={styles.percentage}
                    style={{
                      width: `${percentNumber}%`,
                      backgroundColor: category.bgColor || "#fff",
                    }}
                  />
                  <div className="d-flex align-center">
                    <p className={styles.value}>
                      {Number(category.total).toLocaleString("pt-BR")} clientes
                    </p>
                    <Trend growth={category.growth} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.bubbleChart}>
          <span className={styles.circleA} />
          <span className={styles.circleB} />
          <span className={styles.circleC} />
          <div
            style={{
              width: "100%",
              height: 230,
            }}
          >
            <BubbleChart
              data={{
                name: "root",
                children: categories(data).map((group) => ({
                  name: group.name,
                  value: group.total,
                  color: group.borderColor,
                })),
              }}
              height={230}
            />
          </div>
        </div>
      </div>

      <Drawer
        visible={selectedData}
        onClose={() => setSelectedData(null)}
        withoutTransition
        direction="full-center"
        showCloseButton={false}
      >
        <EngagementGroupChart
          selectedData={selectedData}
          onFetchData={(payload) => fetchGroupHistory(payload)}
          loading={loadingHistoric}
          onClose={() => setSelectedData(false)}
          isCashback={isCashback}
        />
      </Drawer>
    </div>
  );
}
